import { useState } from 'react'
import { Search, Calendar, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { CommissionListParams, DepartmentCommissionListParams, CommissionType, CommissionStatus } from '@/types/commission'
import type { DepartmentScopeValue } from '@/types/department'

interface CommissionFilterBarProps {
  params: CommissionListParams | DepartmentCommissionListParams
  onParamsChange: (params: Partial<CommissionListParams | DepartmentCommissionListParams>) => void
  showDepartmentFilter?: boolean
  showScopeFilter?: boolean
  departments?: { id: number; name: string }[]
  users?: { id: number; nickname: string }[]
}

// 佣金类型选项
const commissionTypeOptions = [
  { value: 'all', label: '全部类型' },
  { value: 'locker', label: '锁客佣金' },
  { value: 'tracker', label: '跟进佣金' },
  { value: 'promoter', label: '推广佣金' },
]

// 佣金状态选项
const statusOptions = [
  { value: '0', label: '全部状态' },
  { value: '1', label: '正常' },
  { value: '2', label: '已扣除' },
]

export function CommissionFilterBar({
  params,
  onParamsChange,
  showDepartmentFilter = false,
  showScopeFilter = false,
  departments = [],
  users = [],
}: CommissionFilterBarProps) {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [scopeValue, setScopeValue] = useState<DepartmentScopeValue>({ scope: 'my' })

  const handleDateChange = (value: { start_date?: string; end_date?: string }) => {
    onParamsChange({
      start_date: value.start_date,
      end_date: value.end_date,
    })
  }

  const clearDateFilter = () => {
    onParamsChange({ start_date: undefined, end_date: undefined })
    setShowDatePicker(false)
  }

  const clearAllFilters = () => {
    onParamsChange({
      commission_type: undefined,
      status: undefined,
      start_date: undefined,
      end_date: undefined,
      order_no: undefined,
    })
    setShowDatePicker(false)
  }

  const hasActiveFilters =
    params.commission_type ||
    params.status ||
    params.start_date ||
    params.end_date ||
    params.order_no

  // Parse current date value for DateRangePicker
  const dateValue =
    params.start_date && params.end_date
      ? { start_date: params.start_date, end_date: params.end_date }
      : undefined

  return (
    <div className="space-y-4">
      {/* 类型 Tab */}
      <div className="flex flex-wrap gap-2">
        {commissionTypeOptions.map((option) => (
          <Badge
            key={option.value}
            variant={params.commission_type === option.value || (!params.commission_type && option.value === 'all') ? 'default' : 'outline'}
            className="cursor-pointer hover:bg-primary/80"
            onClick={() =>
              onParamsChange({
                commission_type: option.value === 'all' ? undefined : (option.value as CommissionType),
              })
            }
          >
            {option.label}
          </Badge>
        ))}
      </div>

      {/* 搜索和筛选 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {/* 部门范围筛选 */}
        {showScopeFilter && (
          <DepartmentScopeFilter
            value={scopeValue}
            onChange={(value) => {
              setScopeValue(value)
              onParamsChange({
                // 本页未启用「无归属」，组件值域实际仍为 my/department
                scope: value.scope as DepartmentCommissionListParams['scope'],
                department_id: value.department_id,
              })
            }}
          />
        )}

        {/* 搜索框 */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="搜索订单号..."
            value={params.order_no || ''}
            onChange={(e) => onParamsChange({ order_no: e.target.value || undefined })}
            className="pl-9"
          />
        </div>

        {/* 状态筛选 */}
        <Select
          value={params.status ? String(params.status) : '0'}
          onValueChange={(value) =>
            onParamsChange({ status: value === '0' ? undefined : (Number(value) as CommissionStatus) })
          }
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="状态" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* 部门筛选 */}
        {showDepartmentFilter && departments.length > 0 && (
          <Select
            value={(params as DepartmentCommissionListParams).department_id ? String((params as DepartmentCommissionListParams).department_id) : 'all'}
            onValueChange={(value) =>
              onParamsChange({
                department_id: value === 'all' ? undefined : Number(value),
              })
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="部门" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部部门</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={String(dept.id)}>
                  {dept.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* 用户筛选 */}
        {showDepartmentFilter && users.length > 0 && (
          <Select
            value={(params as DepartmentCommissionListParams).user_id ? String((params as DepartmentCommissionListParams).user_id) : 'all'}
            onValueChange={(value) =>
              onParamsChange({
                user_id: value === 'all' ? undefined : Number(value),
              })
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="员工" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部员工</SelectItem>
              {users.map((user) => (
                <SelectItem key={user.id} value={String(user.id)}>
                  {user.nickname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* 日期筛选 */}
        <div className="flex items-center gap-2">
          {showDatePicker || params.start_date ? (
            <div className="flex items-center gap-2">
              <DateRangePicker value={dateValue} onChange={handleDateChange} />
              {params.start_date && (
                <Button variant="ghost" size="icon" onClick={clearDateFilter}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setShowDatePicker(true)}>
              <Calendar className="mr-1.5 h-4 w-4" />
              日期筛选
            </Button>
          )}
        </div>

        {/* 清除筛选 */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="mr-1.5 h-4 w-4" />
            清除筛选
          </Button>
        )}
      </div>
    </div>
  )
}
